var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { generateUUID, hasValue } from '@utils';
import { shouldUseCache, getCacheTime, getCacheStore, resolveCacheKey } from './cacheHelper';
import axios from 'axios';
/**
 * An empty/negative result must never be treated as a valid cached value:
 * caching it would serve N/A until the TTL expires with no refetch (see LPVR-357).
 */
export function isEmptyResult(data) {
    if (data === null || data === undefined || data === '')
        return true;
    if (Array.isArray(data))
        return data.length === 0;
    if (typeof data === 'object')
        return Object.keys(data).length === 0;
    return false;
}
/**
 * Check cache and pending requests before execution
 * Returns cached data or pending promise if found, null if needs to execute
 */
export function checkCacheBeforeExecution(cacheKey, cacheManager, pendingRequests, logName, identifier, store = 'memory') {
    if (!cacheKey)
        return null;
    // Check cache. An empty/negative cached value is a miss — evict it so we refetch.
    const cachedData = cacheManager.getCachedData(cacheKey, store);
    if (cachedData !== null && !isEmptyResult(cachedData)) {
        console.log(`Returning cached data for ${logName}:`, identifier);
        return Promise.resolve(cachedData);
    }
    if (cachedData !== null)
        cacheManager.removeCachedData(cacheKey, store);
    // Check for pending requests (deduplication)
    if (pendingRequests.has(cacheKey)) {
        console.log(`Waiting for pending request to complete for ${logName}:`, identifier);
        return pendingRequests.get(cacheKey);
    }
    return null;
}
/**
 * Check cache and pending requests for functions returning ExecutionResult
 * Returns ExecutionResult with cached data if found, null if needs to execute
 */
export function checkCacheBeforeExecutionForResult(cacheKey, cacheManager, pendingRequests, logName, identifier, store = 'memory') {
    if (!cacheKey)
        return null;
    // Check cache. An empty/negative cached value is a miss — evict it so we refetch.
    const cachedData = cacheManager.getCachedData(cacheKey, store);
    if (cachedData !== null && !isEmptyResult(cachedData)) {
        console.log(`Returning cached data for ${logName}:`, identifier);
        return Promise.resolve({ data: cachedData, error: null });
    }
    if (cachedData !== null)
        cacheManager.removeCachedData(cacheKey, store);
    // Check for pending requests (deduplication)
    if (pendingRequests.has(cacheKey)) {
        console.log(`Waiting for pending request to complete for ${logName}:`, identifier);
        return pendingRequests.get(cacheKey);
    }
    return null;
}
/**
 * Store data in cache with logging
 */
export function cacheData(cacheKey, data, cacheManager, cacheTime, logName, identifier, store = 'memory') {
    if (!cacheKey)
        return;
    // Never persist an empty/negative result — a transient empty response would
    // otherwise be served for the full TTL with no refetch (LPVR-357).
    // ponytail: genuinely-empty list results re-query each mount instead of caching; fine.
    if (isEmptyResult(data))
        return;
    cacheManager.setCachedData(cacheKey, data, cacheTime, store);
    console.log(`Cached response for ${logName}:`, identifier, 'for', cacheTime, 'minutes', 'in', store);
}
/**
 * Wrap execution promise with pending request tracking
 */
export function trackPendingRequest(cacheKey, executionPromise, pendingRequests) {
    if (!cacheKey)
        return executionPromise;
    pendingRequests.set(cacheKey, executionPromise);
    executionPromise.finally(() => {
        pendingRequests.delete(cacheKey);
    });
    return executionPromise;
}
/**
 * Generic execution function with caching support
 * Handles cache checking, pending request deduplication, and queue management
 * Returns Promise<any> that rejects on error (legacy behavior)
 */
export function executeBatchWithCaching(cacheType, identifier, itemBuilder, parameters, options, context) {
    const requestId = generateUUID();
    const useCache = shouldUseCache(options);
    const cacheKey = useCache ? resolveCacheKey(options, cacheType, identifier, parameters) : null;
    const store = getCacheStore(options);
    // Check cache and pending requests
    const cachedOrPending = checkCacheBeforeExecution(cacheKey, context.cacheManager, context.pendingRequests, cacheType, identifier, store);
    if (cachedOrPending)
        return cachedOrPending;
    // Handle cancelPrevious
    if (options === null || options === void 0 ? void 0 : options.cancelPrevious) {
        if (hasValue(options === null || options === void 0 ? void 0 : options.key)) {
            context.watchList[options.key] = requestId;
        }
        else {
            console.error("cancelPrevious is enabled, but key is not configured");
        }
    }
    // Create execution promise
    const executionPromise = new Promise((resolve, reject) => {
        const wrappedResolve = (data) => {
            // Cache the response if caching is enabled
            cacheData(cacheKey, data, context.cacheManager, getCacheTime(options), cacheType, identifier, store);
            resolve(data);
        };
        // Build queue item using the provided builder
        const item = itemBuilder(requestId, wrappedResolve, reject);
        // Execute immediately or queue
        if (options === null || options === void 0 ? void 0 : options.executeImmidiately) {
            context._executeQueuedItems(item);
        }
        else {
            context.actionQueue.push(item);
            context.executeQueuedItems();
        }
    });
    // Track pending requests
    return trackPendingRequest(cacheKey, executionPromise, context.pendingRequests);
}
/**
 * Generic execution function with caching support that returns ExecutionResult
 * Never rejects - always resolves with { data, error, errorMessage }
 */
export function executeBatchWithCachingForResult(cacheType, identifier, itemBuilder, parameters, options, context) {
    const requestId = generateUUID();
    const useCache = shouldUseCache(options);
    const cacheKey = useCache ? resolveCacheKey(options, cacheType, identifier, parameters) : null;
    const store = getCacheStore(options);
    // Check cache and pending requests
    const cachedOrPending = checkCacheBeforeExecutionForResult(cacheKey, context.cacheManager, context.pendingRequests, cacheType, identifier, store);
    if (cachedOrPending)
        return cachedOrPending;
    // Handle cancelPrevious
    if (options === null || options === void 0 ? void 0 : options.cancelPrevious) {
        if (hasValue(options === null || options === void 0 ? void 0 : options.key)) {
            context.watchList[options.key] = requestId;
        }
        else {
            console.error("cancelPrevious is enabled, but key is not configured");
        }
    }
    // Create execution promise that never rejects
    const executionPromise = new Promise((resolve) => {
        const wrappedResolve = (data) => {
            // Cache the response if caching is enabled
            cacheData(cacheKey, data, context.cacheManager, getCacheTime(options), cacheType, identifier, store);
            resolve({ data, error: null });
        };
        const wrappedReject = (err) => {
            const errorMessage = err instanceof Error ? err.message : String(err);
            resolve({
                data: null,
                error: err,
                errorMessage
            });
        };
        // Build queue item using the provided builder
        const item = itemBuilder(requestId, wrappedResolve, wrappedReject);
        // Execute immediately or queue
        if (options === null || options === void 0 ? void 0 : options.executeImmidiately) {
            context._executeQueuedItems(item);
        }
        else {
            context.actionQueue.push(item);
            context.executeQueuedItems();
        }
    });
    // Track pending requests
    return trackPendingRequest(cacheKey, executionPromise, context.pendingRequests);
}
/**
 * @export
 * The readable text of a failed request: the server's own error message when
 * the response carries one, otherwise the error's message.
 */
export function getRequestErrorMessage(e) {
    var _a;
    if (axios.isAxiosError(e)) {
        // Try to extract error message from response data
        // Check multiple common error field names (case-insensitive)
        const responseData = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data;
        if (responseData && typeof responseData === 'object') {
            return responseData.ErrorMessage ||
                responseData.errorMessage ||
                responseData.message ||
                responseData.Message ||
                responseData.error ||
                responseData.Error ||
                e.message;
        }
        if (typeof responseData === 'string' && responseData.includes('<html')) {
            const preMatch = responseData.match(/<pre>([\s\S]*?)<\/pre>/);
            if (preMatch) {
                const content = preMatch[1]
                    .replace(/&nbsp;/g, ' ')
                    .replace(/<br\s*\/?>/gi, '\n')
                    .replace(/<[^>]*>/g, '')
                    .trim();
                return content.split('\n')[0].trim() || content;
            }
            const bodyMatch = responseData.match(/<body[^>]*>([\s\S]*?)<\/body>/);
            return bodyMatch
                ? bodyMatch[1].replace(/<[^>]*>/g, '').trim()
                : e.message;
        }
        if (typeof responseData === 'string' && responseData)
            return responseData;
        return e.message;
    }
    if (e instanceof Error)
        return e.message;
    return String(e);
}
// Generic HTTP API wrapper using axios
export function executeAPICall(config) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield axios(config);
            return { data: response.data };
        }
        catch (e) {
            const errorMessage = getRequestErrorMessage(e);
            console.error(`API call to ${config === null || config === void 0 ? void 0 : config.url} failed:`, errorMessage);
            return {
                data: null,
                errorMessage: errorMessage,
                error: e
            };
        }
    });
}
export function normalizeArgs(args) {
    if (!args)
        return {};
    try {
        // String → parse
        if (typeof args === 'string') {
            const parsed = JSON.parse(args);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        }
        // Object → ensure JSON-safe
        if (typeof args === 'object') {
            return JSON.parse(JSON.stringify(args));
        }
        return {};
    }
    catch (_a) {
        return {};
    }
}
